import { useCallback, useEffect, useRef, useState, type JSX, type ReactNode } from "react";

import { ThemeContext, type ResolvedTheme as Resolved, type ThemeChoice } from "@/components/theme";

/**
 * Light / dark / system theme, persisted and applied by toggling `.dark` on
 * <html> (the same class the app and the CSS tokens key off).
 *
 * The bootstrap can't run as an inline <script> — the site's CSP is
 * `script-src 'self'` and would block it — so the class is applied here on
 * mount. index.html ships `class="dark"`, so dark and system-dark viewers see
 * no flash; a light viewer gets one dark frame before this corrects it.
 *
 * A real theme change swaps inside one root View Transition (as the app does),
 * so every token crossfades together instead of cascading out of sync.
 */
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> };
};

const STORAGE_KEY = "openvod.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

const readChoice = (): ThemeChoice => {
  const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
};

const systemResolved = (): Resolved =>
  typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches ? "dark" : "light";

const resolve = (choice: ThemeChoice): Resolved =>
  choice === "system" ? systemResolved() : choice;

const applyResolved = (resolved: Resolved): void => {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
};

const prefersReducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Swap theme tokens as one View Transition so colors don't cascade out of sync. */
const applyWithTransition = (resolved: Resolved): void => {
  const doc = document as ViewTransitionDocument;
  if (prefersReducedMotion() || typeof doc.startViewTransition !== "function") {
    applyResolved(resolved);
    return;
  }
  const root = document.documentElement;
  root.classList.add("theme-transitioning");
  const transition = doc.startViewTransition(() => applyResolved(resolved));
  void transition.finished.finally(() => root.classList.remove("theme-transitioning"));
};

export const ThemeProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [choice, setChoiceState] = useState<ThemeChoice>(readChoice);
  const [resolved, setResolved] = useState<Resolved>(() => resolve(readChoice()));
  /** Last resolved theme actually painted — null until the first mount paint. */
  const paintedRef = useRef<Resolved | null>(null);

  // Apply on mount and whenever the choice changes; when following the system,
  // also react to the OS flipping while the page is open. Only animate when the
  // painted colors truly change (system↔its current resolution is a no-op).
  useEffect(() => {
    const paint = (): void => {
      const next = resolve(choice);
      setResolved(next);
      const changed = paintedRef.current !== null && paintedRef.current !== next;
      paintedRef.current = next;
      if (changed) applyWithTransition(next);
      else applyResolved(next);
    };
    paint();
    if (choice !== "system") return;
    const media = window.matchMedia(DARK_QUERY);
    media.addEventListener("change", paint);
    return () => media.removeEventListener("change", paint);
  }, [choice]);

  const setChoice = useCallback((next: ThemeChoice) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setChoiceState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ choice, resolved, setChoice }}>
      {children}
    </ThemeContext.Provider>
  );
};
